/**
 * Manual Time Calculation Utilities
 * Extracted business logic from manualTimeShowEdit function
 */

import { validateTimeFormat, covertTimeSatmp, splitTime } from "../services/trackerServices.js";

/**
 * Parse time string into hours, minutes, and seconds
 * @param {string} timeString - Time string in HH:MM:SS format
 * @returns {Object|null} Object with hours, minutes, seconds or null if invalid
 */
export const parseTimeString = (timeString) => {
  const parts = timeString.split(":").map(Number);
  
  if (parts.length !== 3) {
    return null;
  }

  const [hours, minutes, seconds] = parts;

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
    return null;
  }

  return { hours, minutes, seconds };
};

/**
 * Calculate total seconds from time components
 * @param {Object} timeComponents - Object with hours, minutes, seconds
 * @returns {number} Total seconds
 */
export const calculateTotalSeconds = ({ hours, minutes, seconds }) => {
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Calculate end time from start time and duration
 * @param {string} date - Date string
 * @param {string} startTime - Start time string
 * @param {number} totalSeconds - Duration in seconds
 * @returns {Date|null} End time as Date object or null if invalid
 */
export const calculateEndTime = (date, startTime, totalSeconds) => {
  // Validate startTime before processing
  if (!startTime || typeof startTime !== "string" || !startTime.trim()) {
    return null;
  }

  const formattedDates = covertTimeSatmp(date, startTime.trim());
  
  if (!formattedDates) {
    return null;
  }

  const timestampInSeconds = Math.floor(new Date(formattedDates).getTime() / 1000);
  const upcomingTimeInSeconds = timestampInSeconds + totalSeconds;
  const upcomingTime = new Date(upcomingTimeInSeconds * 1000);

  if (isNaN(upcomingTime.getTime())) {
    return null;
  }

  return upcomingTime;
};

/**
 * Format time to 12-hour format string
 * @param {Date} date - Date object
 * @returns {string} Formatted time string (HH:MM AM/PM)
 */
export const formatTime12Hour = (date) => {
  return date.toLocaleTimeString(["en-US"], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Process split time (time that spans across days)
 * @param {string} date - Date string
 * @param {string} timeSec - Time string
 * @param {string} startTime - Start time string (optional, defaults to "00:00 AM")
 * @returns {Object|null} Split time result or null if invalid
 */
export const processSplitTime = (date, timeSec, startTime = "00:00 AM") => {
  // Use provided startTime or default to midnight
  const startTimeForCalculation = startTime && startTime.trim() ? startTime.trim() : "00:00 AM";
  
  const formattedDates = covertTimeSatmp(date, startTimeForCalculation);
  
  if (!formattedDates) {
    return null;
  }

  const splitted = splitTime(formattedDates, timeSec);

  if (!splitted?.day1 || !splitted?.day2) {
    return null;
  }

  const formattedTime = new Date(splitted.day2.stop);

  if (isNaN(formattedTime.getTime())) {
    return null;
  }

  return {
    endTime: formatTime12Hour(formattedTime),
    splittedData: splitted,
  };
};

/**
 * Process manual time calculation
 * @param {Object} params - Parameters for time calculation
 * @returns {Object} Result object with endTime, splitted flag, and error flag
 */
export const processManualTime = ({
  timeSec,
  date,
  startTimeManual,
}) => {
  // Check if input is empty or invalid
  if (!timeSec || typeof timeSec !== "string") {
    return {
      success: false,
      error: "INVALID_FORMAT",
      message: "Time string is required",
    };
  }

  // Normalize time string - pad with zeros if needed for partial input
  let normalizedTimeSec = timeSec.trim();
  
  // If empty after trim, return error
  if (!normalizedTimeSec) {
    return {
      success: false,
      error: "INVALID_FORMAT",
      message: "Time string cannot be empty",
    };
  }

  // Allow partial input and pad with zeros
  // "1" -> "01:00:00", "12:" -> "12:00:00", "12:30" -> "12:30:00", "12:30:5" -> "12:30:05"
  const parts = normalizedTimeSec.split(":").map(part => part.trim()).filter(part => part !== "");
  
  // Handle different input formats
  if (parts.length === 0) {
    return {
      success: false,
      error: "INVALID_FORMAT",
      message: "Invalid time format provided",
    };
  } else if (parts.length === 1 && parts[0] && !isNaN(parts[0])) {
    // Only hours provided
    const hours = parseInt(parts[0], 10);
    if (hours >= 0 && hours <= 23) {
      normalizedTimeSec = `${parts[0].padStart(2, "0")}:00:00`;
    } else {
      return {
        success: false,
        error: "INVALID_FORMAT",
        message: "Hours must be between 0 and 23",
      };
    }
  } else if (parts.length === 2 && parts[0] && parts[1] && !isNaN(parts[0]) && !isNaN(parts[1])) {
    // Hours and minutes provided
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      normalizedTimeSec = `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:00`;
    } else {
      return {
        success: false,
        error: "INVALID_FORMAT",
        message: "Hours must be 0-23 and minutes must be 0-59",
      };
    }
  } else if (parts.length === 3 && parts[0] && parts[1] && parts[2] && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
    // All parts provided, ensure proper padding and validate ranges
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59) {
      normalizedTimeSec = `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${parts[2].padStart(2, "0")}`;
    } else {
      return {
        success: false,
        error: "INVALID_FORMAT",
        message: "Hours must be 0-23, minutes and seconds must be 0-59",
      };
    }
  } else {
    // Invalid format - contains non-numeric values or too many parts
    return {
      success: false,
      error: "INVALID_FORMAT",
      message: "Invalid time format provided",
    };
  }

  // Validate normalized time format
  if (!validateTimeFormat(normalizedTimeSec)) {
    return {
      success: false,
      error: "INVALID_FORMAT",
      message: "Invalid time format provided",
    };
  }

  // Parse time string
  const timeComponents = parseTimeString(normalizedTimeSec);
  if (!timeComponents) {
    return {
      success: false,
      error: "INVALID_TIME_COMPONENTS",
      message: "Invalid time components in time string",
    };
  }

  // Check for split time (time spanning multiple days)
  // Use startTimeManual if available, otherwise use default "00:00 AM"
  const startTimeForSplit = startTimeManual && startTimeManual.trim() 
    ? startTimeManual.trim() 
    : "00:00 AM";
  const splitResult = processSplitTime(date, normalizedTimeSec, startTimeForSplit);
  if (splitResult) {
    return {
      success: true,
      endTime: splitResult.endTime,
      splitted: true,
      splittedDate: splitResult.splittedData,
      totalSeconds: null,
    };
  }

  // Validate startTimeManual before calculating end time
  if (!startTimeManual || typeof startTimeManual !== "string" || !startTimeManual.trim()) {
    return {
      success: false,
      error: "INVALID_START_TIME",
      message: "Start time is required and must be a valid string",
    };
  }

  // Calculate end time for single day
  const totalSeconds = calculateTotalSeconds(timeComponents);
  const endTimeDate = calculateEndTime(date, startTimeManual, totalSeconds);

  if (!endTimeDate) {
    return {
      success: false,
      error: "INVALID_END_TIME",
      message: "Failed to calculate valid end time",
    };
  }

  return {
    success: true,
    endTime: formatTime12Hour(endTimeDate),
    splitted: false,
    totalSeconds,
  };
};
